import { useMemo } from 'react';
import type { OutfitProduct } from '../types/ai';

type ChatMessageLike = {
  role: string;
  content?: string;
  outfitProducts?: OutfitProduct[];
  selectedProducts?: OutfitProduct[];
  isOutfitBuilding?: boolean;
};

type WardrobeRole = string;

const LEGACY_CATEGORY_ORDER = ['kavaj', 'skjorta', 'byxa', 'skor'];
const WARDROBE_ROLE_ORDER = [
  'kavaj',
  'jacka',
  'troja',
  'fleece',
  'skjorta',
  't-shirt',
  'byxa',
  'shorts',
  'sandaler',
  'skor',
  'accessoar',
];

const ROLE_CATEGORY_IDS: Record<string, Set<string>> = {
  kavaj: new Set(['1183', '1185']),
  skjorta: new Set(['30', '394', '26']),
  byxa: new Set(['12', '1275']),
  skor: new Set(['233', '231', '228', '236']),
  sandaler: new Set(['234']),
  accessoar: new Set(['371', '372', '373', '374', '375', '376', '488', '1348']),
};

const slugifyCategoryKey = (value: unknown): string | null => {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFC')
    .replace(/[åä]/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || null;
};

const humanizeCategoryLabel = (key: string, fallback?: unknown): string => {
  if (typeof fallback === 'string' && fallback.trim()) return fallback.trim();
  const cleaned = key.replace(/[_-]+/g, ' ').trim();
  if (!cleaned) return 'Plagg';
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

const normalizeRole = (value: unknown): WardrobeRole | null => {
  const token = String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFC');

  if (!token) return null;
  if (token === 'kavaj' || token === 'blazer') return 'kavaj';
  if (token === 'jacket' || token === 'jacka' || token === 'coat' || token === 'shell-jacket')
    return 'jacka';
  if (
    token === 'troja' ||
    token === 'tröja' ||
    token === 'sweater' ||
    token === 'knit' ||
    token === 'pullover' ||
    token === 'cardigan'
  ) {
    return 'troja';
  }
  if (token === 'fleece') return 'fleece';
  if (token === 'skjorta' || token === 'shirt' || token === 'överdel' || token === 'overdel')
    return 'skjorta';
  if (token === 't-shirt' || token === 'tshirt' || token === 'tee' || token === 't shirts') {
    return 't-shirt';
  }
  if (
    token === 'byxa' ||
    token === 'byxor' ||
    token === 'trousers' ||
    token === 'pants' ||
    token === 'underdel'
  ) {
    return 'byxa';
  }
  if (token === 'shorts' || token === 'short') return 'shorts';
  if (token === 'sandaler' || token === 'sandals' || token === 'sandal') return 'sandaler';
  if (
    token === 'skor' ||
    token === 'sko' ||
    token === 'shoe' ||
    token === 'shoes' ||
    token === 'sneakers'
  ) {
    return 'skor';
  }
  if (
    token === 'accessoar' ||
    token === 'accessoarer' ||
    token === 'accessory' ||
    token === 'accessories' ||
    token === 'solglasogon' ||
    token === 'solglasögon'
  ) {
    return 'accessoar';
  }

  return slugifyCategoryKey(token);
};

const inferRoleFromCategoryIds = (categoryIds: unknown): WardrobeRole | null => {
  if (!Array.isArray(categoryIds)) return null;
  const normalized = categoryIds.map((id) => String(id ?? '').trim()).filter((id) => id.length > 0);
  if (normalized.length === 0) return null;

  for (const role of WARDROBE_ROLE_ORDER) {
    if (ROLE_CATEGORY_IDS[role] && normalized.some((id) => ROLE_CATEGORY_IDS[role].has(id))) {
      return role;
    }
  }

  return null;
};

const inferRoleFromName = (name: unknown): WardrobeRole | null => {
  const normalized = String(name ?? '')
    .toLowerCase()
    .normalize('NFC');
  if (!normalized) return null;

  if (/sandal|sandaler|slides?|slippers?/.test(normalized)) {
    return 'sandaler';
  }
  if (
    /sko|skor|shoe|shoes|loafer|derby|sneaker|boot|moccasin|mocassin|\boxford\b(?!\s+shirt)|toe cap/.test(
      normalized,
    )
  ) {
    return 'skor';
  }
  if (/jacka|jacket|shell jacket|windbreaker|anorak|parka|coat/.test(normalized)) return 'jacka';
  if (/fleece/.test(normalized)) return 'fleece';
  if (/kavaj|blazer|suit jacket|suit coat/.test(normalized)) return 'kavaj';
  if (/t-shirt|tshirt|tee\b/.test(normalized)) return 't-shirt';
  if (/shorts?\b/.test(normalized)) return 'shorts';
  if (/byxa|byxor|trouser|chino|pants/.test(normalized)) return 'byxa';
  if (
    /solglas|sunglasses|glasogon|glasses|cap\b|keps|hat\b|hatt|scarf|belt|b[äa]lte|bag|v[äa]ska|towel|handduk|computer case|briefcase|card holder|wallet/.test(
      normalized,
    )
  ) {
    return 'accessoar';
  }
  if (/tr(?:\u00F6|o|\?)ja|stickad|sweater|knit|pullover|cardigan/.test(normalized)) {
    return 'troja';
  }
  if (/skjorta|shirt|oxford shirt|button down|dress shirt/.test(normalized)) {
    return 'skjorta';
  }
  return null;
};

const resolveWardrobeRole = (product: OutfitProduct): WardrobeRole | null => {
  const fromRole = normalizeRole(product.role);
  const fromCategoryIds = inferRoleFromCategoryIds(
    (product as OutfitProduct & { categoryIds?: unknown }).categoryIds,
  );
  const fromName = inferRoleFromName(product.name);
  const fromCategoryKey = normalizeRole(product.categoryKey ?? product.categoryLabel);

  if (fromRole) return fromRole;
  if (fromCategoryKey) return fromCategoryKey;

  if (fromName) return fromName;

  const fromLabel = normalizeRole(product.categoryLabel);
  if (fromLabel) return fromLabel;

  return fromCategoryIds;
};

const compareWardrobeRoles = (left: WardrobeRole, right: WardrobeRole): number => {
  const leftIndex = WARDROBE_ROLE_ORDER.indexOf(left);
  const rightIndex = WARDROBE_ROLE_ORDER.indexOf(right);
  if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right, 'sv');
  if (leftIndex === -1) return 1;
  if (rightIndex === -1) return -1;
  return leftIndex - rightIndex;
};

export type WardrobeViewModel = {
  products: Map<WardrobeRole, OutfitProduct>;
  totalPrice: number;
  isActive: boolean;
  categoryOrder: WardrobeRole[];
  categoryLabels: Record<WardrobeRole, string>;
  isBuilding: boolean;
};

const DEFAULT_BUILDING_ROLES: WardrobeRole[] = ['kavaj', 'skjorta', 'byxa', 'skor', 'accessoar'];
const DEFAULT_BUILDING_LABELS: Record<WardrobeRole, string> = {
  kavaj: 'Kavaj',
  skjorta: 'Skjorta',
  byxa: 'Byxa',
  skor: 'Skor',
  accessoar: 'Accessoar',
};

export function useOutfitWardrobeViewModel(messages: ChatMessageLike[]): WardrobeViewModel {
  return useMemo(() => {
    const products = new Map<WardrobeRole, OutfitProduct>();
    const categoryOrder: WardrobeRole[] = [];
    const categoryLabels: Record<WardrobeRole, string> = {};

    let snapshotStartIndex = 0;
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.role !== 'assistant' || (message.outfitProducts?.length ?? 0) < 2) continue;
      snapshotStartIndex = index;
      break;
    }

    for (const msg of messages.slice(snapshotStartIndex)) {
      if (msg.role !== 'assistant') continue;
      const wardrobeProducts =
        (msg.outfitProducts?.length ? msg.outfitProducts : msg.selectedProducts) ?? [];
      if (!wardrobeProducts.length) continue;
      for (const product of wardrobeProducts) {
        const resolvedRole = resolveWardrobeRole(product);
        if (!resolvedRole) continue;

        if (!categoryOrder.includes(resolvedRole)) {
          categoryOrder.push(resolvedRole);
          categoryOrder.sort(compareWardrobeRoles);
        }
        categoryLabels[resolvedRole] = humanizeCategoryLabel(
          resolvedRole,
          resolvedRole === 'accessoar' ? 'Accessoar' : (product.categoryLabel ?? product.role),
        );

        products.set(resolvedRole, {
          ...product,
          role: resolvedRole,
          categoryKey: resolvedRole,
          categoryLabel: categoryLabels[resolvedRole],
        });
      }
    }

    let totalPrice = 0;
    for (const product of products.values()) {
      totalPrice += product.price ?? 0;
    }

    const lastAssistantMsg = [...messages].reverse().find((m) => m.role === 'assistant');
    const isBuilding = Boolean(lastAssistantMsg?.isOutfitBuilding);

    if (isBuilding) {
      for (const role of DEFAULT_BUILDING_ROLES) {
        if (!categoryOrder.includes(role)) {
          categoryOrder.push(role);
        }
        if (!categoryLabels[role]) {
          categoryLabels[role] = DEFAULT_BUILDING_LABELS[role];
        }
      }
      categoryOrder.sort(compareWardrobeRoles);
    }

    return {
      products,
      totalPrice,
      isActive: products.size > 0 || isBuilding,
      categoryOrder,
      categoryLabels,
      isBuilding,
    };
  }, [messages]);
}
